import { AccordionDetails, Box, Checkbox, FormControlLabel, FormGroup, Grid, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DragIndicator } from '@mui/icons-material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AccountData, Language, PassOrderPageData } from '../../../api';
import { useLanguage } from '../../../common/GeneralUtils';
import { getPassOrderPages } from '../../../utils/AccountUtils';
import { Accordion, AccordionSummary } from '../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { StorefrontPanels } from '../StorefrontDrawer';

type PassOrderPagesPanelProps = {
  account: AccountData;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  expanded?: StorefrontPanels;
  onPanelChange: (panel: StorefrontPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  panelName: StorefrontPanels;
  error?: string;
};

const SortablePassItem = ({
  id,
  page,
  language,
  onToggle,
}: {
  id: string;
  page: PassOrderPageData;
  language: Language;
  onToggle: (id: string) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          {...listeners}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
          <DragIndicator sx={{ color: 'text.secondary', marginInlineEnd: 0.5 }} />
        </Box>
        <FormControlLabel
          control={<Checkbox checked onChange={() => onToggle(page.id)} />}
          label={<Typography>{page.label[language]}</Typography>}
        />
      </Box>
    </div>
  );
};

export const PassOrderPagesPanel = ({
  account,
  selectedIds,
  onChange,
  expanded,
  onPanelChange,
  panelName,
  error,
}: PassOrderPagesPanelProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const passOrderPages = getPassOrderPages(account);

  const passOrderPagesById = new Map(passOrderPages.map((p) => [p.id, p]));
  const selectedPages = selectedIds.map((id) => passOrderPagesById.get(id)).filter(Boolean) as PassOrderPageData[];
  const unselectedPages = passOrderPages.filter((p) => !selectedIds.includes(p.id));

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = selectedIds.indexOf(active.id as string);
      const newIndex = selectedIds.indexOf(over.id as string);
      onChange(arrayMove(selectedIds, oldIndex, newIndex));
    }
  };

  return (
    <Accordion expandedPanelName={expanded} panelName={panelName} onChange={onPanelChange}>
      <AccordionSummary
        title={t('storefronts.pass_order_pages')}
        summaryComponent={<Typography variant="body2">{selectedIds.length}</Typography>}
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={1}>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
          <FormGroup>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={selectedIds} strategy={verticalListSortingStrategy}>
                {selectedPages.map((page) => (
                  <SortablePassItem
                    key={page.id}
                    id={page.id}
                    page={page}
                    language={language}
                    onToggle={handleToggle}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {unselectedPages.map((page) => (
              <FormControlLabel
                key={page.id}
                control={<Checkbox checked={false} onChange={() => handleToggle(page.id)} />}
                label={<Typography>{page.label[language]}</Typography>}
              />
            ))}
          </FormGroup>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
